
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { MapPin, Eye } from 'lucide-react';

interface BreederProfileCardProps {
  profile: {
    id: string;
    name: string;
    profileImage?: string;
    coverImage?: string;
    location: string;
    description: string;
    breeds: string[];
  };
}

const BreederProfileCard: React.FC<BreederProfileCardProps> = ({ profile }) => {
  // Get first letter of name for fallback
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <Card className="hover:shadow-lg transition-shadow h-full">
      <Link to={`/breeder/${profile.id}`}>
        {/* Cover Image Section */}
        <div className="relative bg-stone-200 rounded-t-lg">
          <AspectRatio ratio={4/1} className="rounded-t-lg overflow-hidden">
            {profile.coverImage ? (
              <img 
                src={profile.coverImage} 
                alt="Cover"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600" />
            )}
          </AspectRatio>
          
          {/* Profile Image - Made bigger */}
          <div className="absolute -bottom-10 left-4 z-10">
            <div className="w-20 h-20 md:w-32 md:h-32 rounded-full bg-stone-200 border-4 border-white shadow-lg overflow-hidden">
              {profile.profileImage ? (
                <img 
                  src={profile.profileImage} 
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center">
                  <span className="text-white text-lg font-bold">
                    {getInitials(profile.name)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
      
      <CardContent className="pt-14 px-4 pb-4">
        <div className="mb-3">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-lg line-clamp-1 flex-1 mr-2">{profile.name}</h3>
            <Button 
              variant="outline" 
              size="sm"
              asChild
              className="flex-shrink-0"
            >
              <Link to={`/breeder/${profile.id}`}>
                <Eye className="w-4 h-4 mr-1" />
                View Profile
              </Link>
            </Button>
          </div>
          
          <div className="flex items-center text-gray-600 mb-3">
            <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="text-sm line-clamp-1">{profile.location}</span>
          </div>
        </div>
        
        {/* Breeds badges */}
        {profile.breeds && profile.breeds.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {profile.breeds.slice(0, 3).map((breed, index) => (
              <Badge 
                key={index} 
                className="bg-green-100 text-green-800 hover:bg-green-200 text-xs"
              >
                {breed}
              </Badge>
            ))}
            {profile.breeds.length > 3 && (
              <Badge className="bg-gray-100 text-gray-600 text-xs">
                +{profile.breeds.length - 3} more
              </Badge>
            )}
          </div>
        )}
        
        <p className="text-gray-700 text-sm line-clamp-2 leading-relaxed">
          {profile.description}
        </p>
      </CardContent>
    </Card>
  );
};

export default BreederProfileCard;
